import type { ReactNode } from "react";
import { CollectionStatusEnum } from "@kanpla/api-contract";

import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";

import { collectionStatusIndicator } from "../collection-status-styles";

export type CollectStatus = CollectionStatusEnum | "all";

type StatusOption = {
  key: CollectStatus;
  label: ReactNode;
};

export const STATUS_OPTIONS: StatusOption[] = [
  { key: "all", label: <T _str="Total" /> },
  { key: CollectionStatusEnum.IN_PROGRESS, label: <T _str="Active" /> },
  { key: CollectionStatusEnum.NO_ORDERS, label: <T _str="No order" /> },
  { key: CollectionStatusEnum.COLLECTED, label: <T _str="Collected" /> }
];

export type CollectStatusFilterProps = {
  value: CollectStatus;
  counts: Record<CollectStatus, number>;
  onChange: (value: CollectStatus) => void;
  className?: string;
  disabled?: boolean;
};

/** Segmented status filter for the collect operator list. Uses `rounded-xl` to match the search input. */
export const CollectStatusFilter = ({
  value,
  counts,
  onChange,
  className,
  disabled
}: CollectStatusFilterProps) => (
  <div
    className={cn(
      "inline-flex items-center gap-1 rounded-xl bg-interface-neutral-2 p-1",
      className
    )}
  >
    {STATUS_OPTIONS.map((option) => {
      const active = value === option.key;
      return (
        <button
          key={option.key}
          type="button"
          disabled={disabled}
          onClick={() => {
            onChange(option.key);
          }}
          className={cn(
            "flex items-center gap-2 rounded-lg px-3 py-2 transition-colors",
            {
              "bg-interface-neutral-0 text-interface-neutral-6 shadow-sm":
                active,
              "text-interface-neutral-5 hover:text-interface-neutral-6": !active
            }
          )}
        >
          <span
            className={cn(
              "size-2 flex-none rounded-full",
              option.key === "all"
                ? "bg-interface-neutral-6"
                : collectionStatusIndicator({ status: option.key })
            )}
          />
          <span className="text-base font-bold">{counts[option.key]}</span>
          <span className="whitespace-nowrap text-sm font-semibold">
            {option.label}
          </span>
        </button>
      );
    })}
  </div>
);
